import { useQuery } from '@tanstack/react-query';
import { Badge } from '@/components/ui';
import { usePermissions } from '@/hooks/usePermissions';
import { listEmployeePolicies } from '@/api/services/hrPolicies.service';
import { formatLetterDate } from '../letters/letterFormat';
import { ASSIGNMENT_STATUS } from './policyFormat';

/**
 * The employee's policy acknowledgements in this company, for `hr:policies`
 * holders. Renders nothing without the permission or without any policy.
 */
export function PolicyProfileCard({
  companyId,
  userId,
}: {
  companyId: string;
  userId: string;
}): React.ReactElement | null {
  const { can } = usePermissions();
  const allowed = can('hr', 'policies');
  const { data } = useQuery({
    queryKey: ['hr-employee-policies', companyId, userId],
    queryFn: () => listEmployeePolicies({ companyId }, userId),
    enabled: allowed,
    retry: false,
  });
  const rows = (Array.isArray(data) ? data : []).filter((row) => row.inAudience);
  if (!allowed || rows.length === 0) return null;

  return (
    <section
      aria-labelledby="profile-policies-heading"
      className="rounded-xl border border-gray-200 p-4 dark:border-gray-700"
    >
      <h3
        id="profile-policies-heading"
        className="text-base font-semibold text-gray-900 dark:text-gray-100"
      >
        Policies
      </h3>
      <ul className="mt-3 divide-y divide-gray-200 dark:divide-gray-700">
        {rows.map((row) => {
          const status = row.overdue
            ? { label: 'Overdue', variant: 'danger' as const }
            : ASSIGNMENT_STATUS[row.status];
          return (
            <li key={row._id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm text-gray-900 dark:text-gray-100">
                  {row.policyName}{' '}
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    v{row.versionNumber}
                  </span>
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-300">
                  {row.acknowledgedAt
                    ? `Acknowledged ${formatLetterDate(row.acknowledgedAt)}`
                    : row.dueAt
                      ? `Due ${formatLetterDate(row.dueAt)}`
                      : `Assigned ${formatLetterDate(row.assignedAt)}`}
                </p>
              </div>
              <Badge variant={status.variant}>{status.label}</Badge>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
